import type { DynamicInvoicePageRevisionedLineItem } from '@weft/api-definition';
import type { PromotionLineItem } from '@weft/types';
import { DEFAULT_CURRENCY, INVOICE_REVIEW_STATUSES, REVISIONED_LINE_ITEM_TYPES } from '@weft/types';
import { useDeleteRevisionedLineItem } from 'apps/app/src/hooks/requests/use-delete-revisioned-line-item';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import { DynamicInvoiceRevisionedLineItem } from '../dynamic-invoice-line-Item/DynamicInvoiceRevisionedLineItem';
import { DynamicInvoiceViewSection } from '../DynamicInvoiceViewSection';
import type { RevisionedLineItemDialogForms } from '../revisioned-line-item-dialog-forms/revisioned-line-item-dialog-forms-types/revisioned-line-item-dialog-type';
import { RevisionedLineItemDialogs } from '../revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';
import type { RevisionedLineItemDialogType } from '../revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';

type RevisionedLineItemsSectionProps = {
  invoiceId: string;
  queryKeyToInvalidate: string[];
  promotionLineItems: PromotionLineItem[]
  revisionedLineItems: DynamicInvoicePageRevisionedLineItem[];
  onRevisionedLineItemClick?: (revisionedLineItemDialogType: RevisionedLineItemDialogType, lineItemId?: string, defaultValues?: RevisionedLineItemDialogForms[typeof revisionedLineItemDialogType]) => () => void;
};

export const RevisionedLineItemsSection: React.FC<RevisionedLineItemsSectionProps> = (
  { revisionedLineItems, onRevisionedLineItemClick, queryKeyToInvalidate }) => {
  const fixedRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.Fixed);
  const fixedPromotionRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.FixedPromotion);
  const percentagePromotionRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.PercentagePromotion);
  const queryClient = useQueryClient();
  const deleteFixedRevisionedLineItemMutation = useDeleteRevisionedLineItem();

  const onDelete = async (revisionedLineItemId: string) => {
    await runMutation(
      deleteFixedRevisionedLineItemMutation,
      { revisionedLineItemId },
      {
        pending: 'Deleting line item...',
        success: 'Deleted line item',
        error: 'Failed to delete',
      },
      {
        onSuccess: () => queryClient.invalidateQueries(queryKeyToInvalidate),
      },
    );
  };

  const { dynamicInvoiceData } = useDynamicInvoice();

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const isEditDisabled = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;

  return (
    <DynamicInvoiceViewSection
      title="Revisions"
    >
      {fixedRevisionedLineItems?.map((fixedRevisionedLineItem) => (
        <DynamicInvoiceRevisionedLineItem
          disabled={isEditDisabled}
          key={fixedRevisionedLineItem.id}
          item={fixedRevisionedLineItem}
          onClick={onRevisionedLineItemClick?.(RevisionedLineItemDialogs.Fixed, fixedRevisionedLineItem.id, fixedRevisionedLineItem)}
          onDeleteClick={onDelete}
          currency={currency}
        />
      ))}

      {fixedPromotionRevisionedLineItems?.map((fixedPromotionRevisionedLineItem) => (
        <DynamicInvoiceRevisionedLineItem
          disabled={isEditDisabled}
          key={fixedPromotionRevisionedLineItem.id}
          item={fixedPromotionRevisionedLineItem}
          onClick={onRevisionedLineItemClick?.(RevisionedLineItemDialogs.Promotion, fixedPromotionRevisionedLineItem.id, fixedPromotionRevisionedLineItem)}
          onDeleteClick={onDelete}
          currency={currency}
        />
      ))}

      {percentagePromotionRevisionedLineItems?.map((percentagePromotionRevisionedLineItem) => (
        <DynamicInvoiceRevisionedLineItem
          disabled={isEditDisabled}
          key={percentagePromotionRevisionedLineItem.id}
          item={percentagePromotionRevisionedLineItem}
          onClick={onRevisionedLineItemClick?.(RevisionedLineItemDialogs.Promotion, percentagePromotionRevisionedLineItem.id, percentagePromotionRevisionedLineItem)}
          onDeleteClick={onDelete}
          currency={currency}
        />
      ))}
    </DynamicInvoiceViewSection>
  );
};